import type {MessageDescriptor} from 'react-intl';
import {defineMessages} from 'react-intl';
import type {Mapper} from '@automapper/core';
import type {CreateRepaymentLogInput, EditRepaymentLogInput} from '@igp/shared/contract/pam';
import {map, mergeMap} from '@igp/shared/otel';
import {BaseEpicsBuilder} from '@igp/shared/redux';
import type {INormalized} from '@igp/shared/redux/entity';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import {EntityType} from '@models';
import {entityActions, messageActions, realtimeActions} from '@redux';
import {inject, injectable} from 'inversify';
import type {Epic} from 'redux-observable';
import {filter, of} from 'rxjs';
import {isActionOf} from 'typesafe-actions';

import {ServiceTypes} from '@ioc-container';

import {repaymentLogActions} from './actions';
import {repaymentLogMappingKeys} from './mapper';
import type {IRepaymentLogService} from './service';
import type {CreateRepaymentLogPayload, EditRepaymentLogPayload, RepaymentLogErrorCodes} from './types';
import {RepaymentLogServiceTypes} from './types';

export const localized = defineMessages({
    createSuccess: {
        id: 'RepaymentLogEpicsBuilder_createSuccess',
        defaultMessage: 'Repayment entry has been successfully created',
    },
    createFailure: {
        id: 'RepaymentLogEpicsBuilder_createFailure',
        defaultMessage: 'Failed to create Repayment entry',
    },
    editSuccess: {
        id: 'RepaymentLogEpicsBuilder_editSuccess',
        defaultMessage: 'Repayment entry has been successfully updated',
    },
    editFailure: {
        id: 'RepaymentLogEpicsBuilder_editFailure',
        defaultMessage: 'Failed to update Repayment entry',
    },
    removeSuccess: {
        id: 'RepaymentLogEpicsBuilder_removeSuccess',
        defaultMessage: 'Repayment entry has been successfully removed',
    },
    removeFailure: {
        id: 'RepaymentLogEpicsBuilder_removeFailure',
        defaultMessage: 'Failed to remove Repayment entry',
    },
    playerIsNotMasterAgent: {
        id: 'RepaymentLogEpicsBuilder_playerIsNotMasterAgent',
        defaultMessage: 'The Master Agent ID is not a valid master agent. Please verify and try again.',
    },
    agentIsNotDownline: {
        id: 'RepaymentLogEpicsBuilder_agentIsNotDownline',
        defaultMessage: 'The specified From Agent is not in the downline hierarchy of the selected Master Agent.',
    },
    logNotFound: {
        id: 'RepaymentLogEpicsBuilder_logNotFound',
        defaultMessage: 'Reapyment log is not found',
    },
});

@injectable()
export class RepaymentLogEpicsBuilder extends BaseEpicsBuilder {
    constructor(
        @inject(ServiceTypes.AutoMapper) private readonly _mapper: Mapper,
        @inject(RepaymentLogServiceTypes.service) private readonly _service: IRepaymentLogService
    ) {
        super();
    }

    protected buildEpicList(): Epic[] {
        return [
            this.createRepaymentLogRequestEpic(),
            this.createRepaymentLogSuccessEpic(),
            this.createRepaymentLogFailureEpic(),
            this.editRepaymentLogRequestEpic(),
            this.editRepaymentLogSuccessEpic(),
            this.editRepaymentLogFailureEpic(),
            this.removeRepaymentLogRequestEpic(),
            this.removeRepaymentLogSuccessEpic(),
            this.removeRepaymentLogFailureEpic(),
        ];
    }

    private createRepaymentLogRequestEpic(): Epic {
        return this.buildRequestEpic(repaymentLogActions.create, payload => {
            const input = this._mapper.map<CreateRepaymentLogPayload, CreateRepaymentLogInput>(
                payload,
                repaymentLogMappingKeys.createPayload,
                repaymentLogMappingKeys.createInput
            );
            return this._service.create(input);
        });
    }

    private createRepaymentLogSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(repaymentLogActions.create.success)),
                mergeMap(() =>
                    of(
                        messageActions.saveMessage({
                            message: localized.createSuccess,
                            mode: 'success',
                        }),
                        realtimeActions.event({
                            entity: EntityType.RepaymentLog,
                            items: [],
                            trigger: {
                                type: RealtimeMessageTrigger.Add,
                            },
                        })
                    )
                )
            );
    }

    private createRepaymentLogFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(repaymentLogActions.create.failure)),
                map(({payload}) => {
                    const errorCode = payload.errors?.[0]?.code;
                    return messageActions.saveMessage({
                        message: this.errorCodeMapping[errorCode as RepaymentLogErrorCodes] ?? localized.createFailure,
                        mode: 'error',
                    });
                })
            );
    }

    private editRepaymentLogRequestEpic(): Epic {
        return this.buildRequestEpic(repaymentLogActions.edit, payload => {
            const input = this._mapper.map<EditRepaymentLogPayload, EditRepaymentLogInput>(
                payload,
                repaymentLogMappingKeys.editPayload,
                repaymentLogMappingKeys.editInput
            );
            return this._service.edit(input);
        });
    }

    private editRepaymentLogSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(repaymentLogActions.edit.success)),
                mergeMap(({payload}) => {
                    const item = payload.requestPayload;
                    return of(
                        messageActions.saveMessage({
                            message: localized.editSuccess,
                            mode: 'success',
                        }),
                        entityActions.updateItem({
                            type: EntityType.RepaymentLog,
                            id: item?.repayment_id,
                            updatedItem: item as INormalized,
                        })
                    );
                })
            );
    }

    private editRepaymentLogFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(repaymentLogActions.edit.failure)),
                map(({payload}) => {
                    const errorCode = payload.errors?.[0]?.code;
                    return messageActions.saveMessage({
                        message: this.errorCodeMapping[errorCode as RepaymentLogErrorCodes] ?? localized.editFailure,
                        mode: 'error',
                    });
                })
            );
    }
    private removeRepaymentLogRequestEpic(): Epic {
        return this.buildRequestEpic(repaymentLogActions.remove, payload => {
            return this._service.remove(payload);
        });
    }

    private removeRepaymentLogSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(repaymentLogActions.remove.success)),
                mergeMap(() =>
                    of(
                        messageActions.saveMessage({
                            message: localized.removeSuccess,
                            mode: 'success',
                        }),
                        realtimeActions.event({
                            entity: EntityType.RepaymentLog,
                            items: [],
                            trigger: {type: RealtimeMessageTrigger.Delete},
                        })
                    )
                )
            );
    }

    private removeRepaymentLogFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(repaymentLogActions.remove.failure)),
                map(({payload}) => {
                    const errorCode = payload.errors?.[0]?.code;
                    return messageActions.saveMessage({
                        message: this.errorCodeMapping[errorCode as RepaymentLogErrorCodes] ?? localized.removeFailure,
                        mode: 'error',
                    });
                })
            );
    }

    private errorCodeMapping: Record<RepaymentLogErrorCodes, MessageDescriptor> = {
        PLAYER_IS_NOT_MASTER_AGENT: localized.playerIsNotMasterAgent,
        AGENT_IS_NOT_DOWNLINE_OF_MASTER_AGENT: localized.agentIsNotDownline,
        REPAYMENT_LOG_NOT_FOUND: localized.logNotFound,
    };
}
